import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Zap } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href: string) => {
    if (!isHomePage) {
      navigate('/' + href);
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { label: 'Model spolupráce', href: '/model-spoluprace', isPage: true },
    { label: 'Ceny', href: '#pricing', isPage: false },
    { label: 'Blog', href: '/blog', isPage: true },
    { label: 'O nás', href: '/o-nas', isPage: true },
    { label: 'Kariéra', href: '/kariera', isPage: true },
  ];

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'glass-strong py-3 shadow-lg shadow-slate-200/50'
          : 'bg-transparent py-5'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            onClick={(e) => {
              if (isHomePage) {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            className="flex items-center gap-3 group"
          >
            <motion.div
              className="relative w-10 h-10 flex items-center justify-center"
              whileHover={{ scale: 1.05, rotate: 5 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#0066FF] to-[#00D4AA] rounded-xl opacity-90 group-hover:opacity-100 transition-opacity" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#0066FF] to-[#00D4AA] rounded-xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity" />
              <Zap className="relative w-5 h-5 text-white" />
            </motion.div>
            <span className="text-xl font-bold text-slate-800">
              AI<span className="text-gradient">Visibility</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link, index) => (
              <motion.div
                key={link.href}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }}
              >
                {link.isPage ? (
                  <Link
                    to={link.href}
                    className="relative px-4 py-2 text-sm text-slate-600 hover:text-slate-900 transition-colors group"
                  >
                    {link.label}
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-[#0066FF] to-[#00D4AA] group-hover:w-4/5 transition-all duration-300 rounded-full" />
                  </Link>
                ) : (
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="relative px-4 py-2 text-sm text-slate-600 hover:text-slate-900 transition-colors group"
                  >
                    {link.label}
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-[#0066FF] to-[#00D4AA] group-hover:w-4/5 transition-all duration-300 rounded-full" />
                  </button>
                )}
              </motion.div>
            ))}
          </div>

          {/* CTA Button */}
          <motion.div
            className="hidden md:block"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
          >
            <Link to="/audit">
              <motion.button
                className="relative px-6 py-2.5 rounded-xl text-sm font-semibold text-white overflow-hidden group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-[#0066FF] to-[#00D4AA]" />
                <span className="absolute inset-0 bg-gradient-to-r from-[#0066FF] to-[#00D4AA] blur-lg opacity-40 group-hover:opacity-60 transition-opacity" />
                <span className="relative z-10">AI Audit</span>
              </motion.button>
            </Link>
          </motion.div>

          {/* Mobile Menu Button */}
          <motion.button
            className="md:hidden text-slate-700 p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            whileTap={{ scale: 0.95 }}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className="md:hidden mt-4 py-4 glass-strong rounded-2xl overflow-hidden shadow-xl shadow-slate-200/50"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-col gap-2 px-4">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    {link.isPage ? (
                      <Link
                        to={link.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block text-left text-slate-600 hover:text-slate-900 transition-colors py-3 px-4 rounded-xl hover:bg-slate-100/50"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <button
                        onClick={() => scrollToSection(link.href)}
                        className="block w-full text-left text-slate-600 hover:text-slate-900 transition-colors py-3 px-4 rounded-xl hover:bg-slate-100/50"
                      >
                        {link.label}
                      </button>
                    )}
                  </motion.div>
                ))}
                <Link to="/audit" onClick={() => setIsMobileMenuOpen(false)}>
                  <motion.button
                    className="w-full mt-2 py-3 rounded-xl bg-gradient-to-r from-[#0066FF] to-[#00D4AA] text-white font-semibold"
                    whileTap={{ scale: 0.98 }}
                  >
                    AI Audit
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
